import { useState, type FormEvent } from 'react'
import { MessageCircle, Send } from 'lucide-react'
import { ApiClientError, apiFetch } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ChatResponse {
  reply: string
  conversationId: string
}

type TravelDeskMode = 'crew' | 'artist'

export function TravelDeskChat({ mode }: { mode: TravelDeskMode }) {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const endpoint =
    mode === 'crew' ? '/crew/profile/travel-desk/chat' : '/travel/travel-desk/chat'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!input.trim()) return
    const userMsg: ChatMessage = { role: 'user', content: input }
    setMessages((m) => [...m, userMsg])
    setInput('')
    setSubmitting(true)
    setError(null)
    try {
      const fetcher = mode === 'crew' ? apiFetch<ChatResponse> : artistFetch<ChatResponse>
      const data = await fetcher(endpoint, {
        method: 'POST',
        body: { message: userMsg.content, conversationId },
      })
      setConversationId(data.conversationId)
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }])
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not reach travel desk.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="rounded-xl border border-ovation-ink/10 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-ovation-ink" />
        <h2 className="font-display text-lg">Tour travel desk</h2>
      </div>
      <p className="mb-4 text-sm text-ovation-muted">
        {mode === 'crew'
          ? 'Ask about your flights, hotel, show call times, and who to contact if plans change. Text chat only — no voice. Off-topic questions are blocked.'
          : 'Ask about party travel, hotels, and show logistics for the touring team. Text chat only — off-topic questions are blocked.'}
      </p>

      <div className="mb-3 max-h-64 space-y-3 overflow-y-auto rounded-lg border border-ovation-ink/10 bg-ovation-ink/5 p-3">
        {messages.length === 0 && (
          <p className="text-sm text-ovation-muted">
            Try: <em>Where am I staying for the Chicago show?</em> or{' '}
            <em>What time do I need to leave for the airport?</em>
          </p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[90%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-ovation-ink text-ovation-paper'
                  : 'border border-ovation-ink/10 bg-white'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {submitting && <p className="text-sm text-ovation-muted">Checking your tour data…</p>}
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about travel or show logistics…"
          className="flex-1 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          disabled={submitting}
        />
        <button
          type="submit"
          disabled={submitting || !input.trim()}
          className="inline-flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          Ask
        </button>
      </form>
    </section>
  )
}
